"use client";

import {NeoAGgrid} from "@/components/table/neo-table/neo-ag-grid";
import {formatDateTime, formatTime} from "@/libs/helper";
import {ManageBookingItem} from "@/types/booking";
import {useState} from "react";
import {FiClock, FiNavigation, FiTrash2, FiX} from "react-icons/fi";

export type PassengerStatus = "Paid" | "Unpaid";

type PassengerDetail = {
  id: string;
  name: string;
  image: string;
  status: PassengerStatus;
  boardingPoint: string;
  dropPoint: string;
  reason: string;
  travelFrom: string;
  travelTo: string;
  travelDate: string;
  travelledTime: string;
};

export type BookingUiDetail = {
  seatsFilled: number;
  totalSeats: number;
  routeName: string;
  busStartTime: string;
  busEndTime: string;
  totalTravelTime: string;
  liveStatus: string;
  liveTrackingAvailable: boolean;
  passengers: PassengerDetail[];
};

export const getManageBookingFallbackRows = (statusType?: number) => {
  switch (statusType) {
    case 1:
      return [
        {
          name: "Demo User 1",
          image: "/images/avatar.png",
          bookingId: 2001,
          vehicleName: "TN01A9934",
          bookingTime: new Date("2026-05-28T09:00:00").getTime(),
          driverName: "John Driver",
          driverEmpId: "SAMP062",
          pickupPoint: "KG Cinemas Stop",
          dropPoint: "Test Institute Branch 1"
        },
        {
          name: "Demo User 2",
          image: "/images/avatar.png",
          bookingId: 2002,
          vehicleName: "TN38DE2312",
          bookingTime: new Date("2026-05-28T10:00:00").getTime(),
          driverName: "Arun Driver",
          driverEmpId: "SAMP063",
          pickupPoint: "Hope College",
          dropPoint: "NeoSay Campus"
        }
      ];
    case 2:
      return [
        {
          name: "Demo User 1",
          image: "/images/avatar.png",
          bookingId: 3001,
          vehicleName: "TN01AB1234",
          bookingTime: new Date("2026-06-01T09:00:00").getTime(),
          driverName: "Kumar Driver",
          driverEmpId: "SAMP070",
          pickupPoint: "Gandhipuram",
          dropPoint: "NeoSay Campus"
        },
        {
          name: "Demo User 2",
          image: "/images/avatar.png",
          bookingId: 3002,
          vehicleName: "TN38XY5678",
          bookingTime: new Date("2026-06-01T10:30:00").getTime(),
          driverName: "Ravi Driver",
          driverEmpId: "SAMP071",
          pickupPoint: "Peelamedu",
          dropPoint: "Airport"
        }
      ];
    case 3:
      return [
        {
          name: "Demo User 1",
          image: "/images/avatar.png",
          bookingId: 3001,
          vehicleName: "TN09Z4455",
          bookingTime: new Date("2026-05-27T08:30:00").getTime(),
          driverName: "Vignesh Driver",
          driverEmpId: "SAMP071",
          pickupPoint: "Saibaba Colony",
          dropPoint: "NeoSay Campus"
        },
        {
          name: "Demo User 2",
          image: "/images/avatar.png",
          bookingId: 3002,
          vehicleName: "TN66BY2295",
          bookingTime: new Date("2026-05-27T18:00:00").getTime(),
          driverName: "Kumar Driver",
          driverEmpId: "SAMP072",
          pickupPoint: "Town Hall",
          dropPoint: "Saravanampatti"
        }
      ];
    case 4:
      return [
        {
          name: "Demo User 1",
          image: "/images/avatar.png",
          bookingId: 4001,
          vehicleName: "TN07AB1200",
          bookingTime: new Date("2026-05-26T09:30:00").getTime(),
          driverName: "Raja Driver",
          driverEmpId: "SAMP081",
          pickupPoint: "Gandhipuram",
          dropPoint: "Peelamedu",
          comments: "Booking cancelled by user request"
        },
        {
          name: "Demo User 2",
          image: "/images/avatar.png",
          bookingId: 4002,
          vehicleName: "TN11CD3400",
          bookingTime: new Date("2026-05-26T17:45:00").getTime(),
          driverName: "Mani Driver",
          driverEmpId: "SAMP082",
          pickupPoint: "Ukkadam",
          dropPoint: "Singanallur",
          comments: "Trip cancelled due to route change"
        }
      ];
    case 5:
      return [
        {
          name: "Demo User 1",
          image: "/images/avatar.png",
          bookingId: 5001,
          vehicleName: "TN01AB1234",
          bookingTime: new Date("2026-05-28T09:00:00").getTime(),
          driverName: "Kumar Driver",
          driverEmpId: "SAMP080",
          pickupPoint: "Gandhipuram",
          dropPoint: "NeoSay Campus"
        },
        {
          name: "Demo User 2",
          image: "/images/avatar.png",
          bookingId: 5002,
          vehicleName: "TN38XY5678",
          bookingTime: new Date("2026-05-28T10:30:00").getTime(),
          driverName: "Ravi Driver",
          driverEmpId: "SAMP081",
          pickupPoint: "Peelamedu",
          dropPoint: "Airport"
        }
      ];
    default:
      return [];
  }
};

const formatInputDate = (timestamp: number) => {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getBookingUiDetail = (
  booking: ManageBookingItem,
  statusType?: number
): BookingUiDetail => {
  const seed = Number(booking.bookingId || 0);
  const totalSeats = 24;
  const seatsFilled = 10 + (seed % 11);
  const routeName = `Route-${String((seed % 12) + 1).padStart(2, "0")}`;
  const busStartTime = formatDateTime(booking.bookingTime);
  const busEndTime = formatDateTime(booking.bookingTime + 45 * 60 * 1000);
  const totalTravelTime = `${25 + (seed % 21)} mins`;
  const liveTrackingAvailable = seed % 4 !== 0;
  const liveStatus = !liveTrackingAvailable
    ? "Tracking unavailable"
    : seatsFilled >= 22
      ? "Near full"
      : seatsFilled >= 16
        ? "Healthy load"
        : "Comfortable load";

  const passengers: PassengerDetail[] = Array.from({length: 5}, (_, index) => {
    const isPaid = (seed + index) % 3 !== 0;
    const reason =
      statusType === 4
        ? index === 0 && booking.comments
          ? booking.comments
          : "Booking cancelled by user request"
        : statusType === 5
          ? index === 0 && booking.pickupMissed
            ? `Pickup missed at ${formatTime(booking.pickupMissed)}`
            : "Passenger did not board"
          : isPaid
            ? "Fare cleared successfully"
            : "Payment pending";

    return {
      id: `${booking.bookingId}-${index + 1}`,
      name:
        index === 0
          ? booking.name
          : `Passenger ${String.fromCharCode(65 + ((seed + index) % 26))}${index + 1}`,
      image:
        index === 0 && booking.image && booking.image.trim() !== ""
          ? booking.image
          : "/images/avatar.png",
      status: isPaid ? "Paid" : "Unpaid",
      boardingPoint:
        index === 0
          ? booking.pickupPoint
          : `${booking.pickupPoint} Stop ${index + 1}`,
      dropPoint:
        index === 0
          ? booking.dropPoint
          : `${booking.dropPoint} Gate ${index + 1}`,
      reason,
      travelFrom: formatTime(booking.bookingTime + index * 4 * 60 * 1000),
      travelTo: formatTime(
        booking.bookingTime + (index * 4 + 12 + index * 5) * 60 * 1000
      ),
      travelDate: formatInputDate(booking.bookingTime + index * 4 * 60 * 1000),
      travelledTime: `${12 + index * 5} mins`
    };
  });

  return {
    seatsFilled,
    totalSeats,
    routeName,
    busStartTime,
    busEndTime,
    totalTravelTime,
    liveStatus,
    liveTrackingAvailable,
    passengers
  };
};

export const ManageBookingDetailView = ({
  booking,
  statusType,
  backHref,
  onClose
}: {
  booking: ManageBookingItem;
  statusType: number;
  backHref?: string;
  onClose?: () => void;
}) => {
  const uiDetail = getBookingUiDetail(booking, statusType);
  const isOngoing = statusType === 1;
  const isUpcoming = statusType === 2;
  const isCancelled = statusType === 4;
  const [passengers, setPassengers] = useState(() => uiDetail.passengers);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const filteredPassengers = passengers;

  const handleRemovePassenger = (id: string) => {
    setPassengers(prev => prev.filter(passenger => passenger.id !== id));
    setConfirmRemoveId(null);
  };

  const passengerTable = {
    columns: [
      {uid: "image", name: "Photo"},
      {uid: "name", name: "Name"},
      {uid: "id", name: "Booking ID"},
      {uid: "status", name: "Payment Status"},
      {uid: "boardingPoint", name: "Boarding Point"},
      {uid: "dropPoint", name: "Drop Point"},
      {uid: "travelDate", name: "Travel Date"},
      ...(isCancelled ? [{uid: "reason", name: "Reason"}] : []),
      {uid: "travel", name: "Travel"}
    ],
    activeColumns: [] as string[],
    tabledatas: filteredPassengers.map(passenger => ({
      ...passenger,
      travel: `${passenger.travelFrom} - ${passenger.travelTo} | ${passenger.travelledTime}`
    })),
    style: {
      height: "h-[320px]"
    },
    meta: {
      id: 0,
      title: "",
      description: "",
      hasSearch: false,
      hasUpload: false,
      hasDownload: false,
      hasAdd: false,
      hasActions: false,
      hasCheckbox: false,
      hasFilter: false,
      hasHeader: false,
      hasFooter: false,
      hasPagination: false,
      hasCreate: false
    },
    url: {
      page: {},
      api: {
        get: ""
      }
    }
  };

  passengerTable.activeColumns = passengerTable.columns.map(
    column => column.uid
  );

  return (
    <div className="flex min-h-[calc(100vh-63px)] flex-col px-3 py-3">
      <div className=" flex flex-row gap-2 pb-2">
        <div className="font-bold">Booking Details - </div>
        <div className="  font-semibold ">{booking.vehicleName}</div>
      </div>

      <div className="flex w-fit items-center gap-6 overflow-x-auto rounded-xl border border-slate-200 px-6 py-3">
        <div className="shrink-0 border-r border-slate-200 pr-6">
          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
            <FiClock className="h-3.5 w-3.5" />
            <span>Trip Timing</span>
          </div>
          <div className="mt-1 whitespace-nowrap text-sm text-slate-800">
            {uiDetail.busStartTime} - {uiDetail.busEndTime}
          </div>
        </div>

        <div className="shrink-0 border-r border-slate-200 pr-4">
          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
            <FiNavigation className="h-3.5 w-3.5" />
            <span>Route</span>
          </div>
          <div className="mt-1 whitespace-nowrap text-sm text-slate-800">
            {booking.pickupPoint} {"->"} {booking.dropPoint}
          </div>
        </div>

        <div className="shrink-0 border-r border-slate-200 pr-4">
          <div className="text-xs font-medium text-blue-500">Seat Filled</div>
          <div className="mt-1 text-sm font-semibold text-blue-700">
            {uiDetail.seatsFilled}/{uiDetail.totalSeats}
          </div>
        </div>

        <div className="shrink-0 border-r border-slate-200 pr-4">
          <div className="whitespace-nowrap text-xs font-medium text-slate-500">
            Total Bus Travel Time
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-800">
            {uiDetail.totalTravelTime}
          </div>
        </div>

        <div className="shrink-0 pr-4">
          <div className="text-xs font-medium text-slate-500">Driver</div>
          <div className="mt-1 whitespace-nowrap text-sm font-semibold text-slate-800">
            {booking.driverName} ({booking.driverEmpId})
          </div>
        </div>
      </div>

      <div className="mt-4">
        {isUpcoming ? (
          <div className="mt-3 max-h-[320px] overflow-auto rounded-2xl border border-slate-200">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="sticky top-0 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2.5">Photo</th>
                  <th className="px-4 py-2.5">Name</th>
                  <th className="px-4 py-2.5">Booking ID</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Boarding Point</th>
                  <th className="px-4 py-2.5">Drop Point</th>
                  <th className="px-4 py-2.5">Travel Date</th>
                  <th className="px-4 py-2.5">Travel</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {passengers.map(passenger => (
                  <tr key={passenger.id}>
                    <td className="px-4 py-2.5">
                      <img
                        src={passenger.image}
                        alt={passenger.name}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    </td>
                    <td className="px-4 py-2.5 font-medium text-slate-900">
                      {passenger.name}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {passenger.id}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          passenger.status === "Paid"
                            ? "bg-emerald-50 text-emerald-600"
                            : "bg-rose-50 text-rose-600"
                        }`}
                      >
                        {passenger.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {passenger.boardingPoint}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {passenger.dropPoint}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {passenger.travelDate}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {passenger.travelFrom} - {passenger.travelTo} |{" "}
                      {passenger.travelledTime}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1.5">
                        {confirmRemoveId === passenger.id ? (
                          <>
                            <span className="text-xs font-medium text-slate-500">
                              Remove?
                            </span>
                            <button
                              type="button"
                              className="rounded-full bg-rose-600 px-2 py-1 text-xs font-semibold text-white transition-colors hover:bg-rose-700"
                              onClick={() =>
                                handleRemovePassenger(passenger.id)
                              }
                            >
                              Yes
                            </button>
                            <button
                              type="button"
                              className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                              onClick={() => setConfirmRemoveId(null)}
                              aria-label="Cancel remove"
                            >
                              <FiX className="h-3.5 w-3.5" />
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="flex h-7 w-7 items-center justify-center rounded-full text-rose-500 transition-colors hover:bg-rose-50"
                            onClick={() => setConfirmRemoveId(passenger.id)}
                            aria-label={`Remove ${passenger.name}`}
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {passengers.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-6 text-center text-sm text-slate-400"
                    >
                      No passengers left on this booking.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200">
            <div className="h-[320px] w-full">
              <NeoAGgrid table={passengerTable} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
